import * as React from 'react'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { Flex, SimpleSpinner, VisuallyHidden } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { range } from '~publish/helpers/range'

import type { BaseCommunityFilters } from '../../CommunityFilters/types'
import { useFilters } from '../../CommunityFilters/useFilters'
import { MentionContextProvider } from '../context'
import type { MentionData } from '../context'
import { MentionCard, MentionCardSkeleton } from '../MentionCard'
import {
  MentionsNeedsRefreshEmptyState,
  MentionsNotSupportedEmptyState,
} from '../MentionsEmptyState'
import { MentionsError } from '../MentionsError'

import {
  useMentionsData,
  useMentionListCoordination,
  useMentionsEmptyState,
} from './hooks'
import {
  EngagementCoordinationProvider,
  useEngagementCoordinationStore,
} from '../../context/EngagementCoordinationContext'
import {
  selectIsExpanded,
  selectIsLocallyResolved,
} from '../../hooks/engagement/engagementCoordinationStore'
import { useCommunityConfig } from '../../CommunityConfig'
import { useMentionsNeedsRefresh } from '../../hooks/mentions/useMentionsNeedsRefresh'
import { useChannelMap } from '../../hooks/useChannelMap'
import { useMentionRepliesBatch } from '../../hooks/mentions/useMentionRepliesBatch'
import { useNewEngagementBoundary } from '../../hooks/engagement/useNewEngagementBoundary'
import { useScrollToTop } from '../../hooks/useScrollToTop'
import { NewItemsDivider } from '../../components/NewItemsDivider'
import { NoMediaHint, useNoMediaChannels } from '../../components/NoMediaHint'

import styles from './MentionsList.module.css'

import type { JSX } from 'react'

export const MentionsList_Channel = graphql(`
  fragment MentionsList_Channel on Channel @_unmask {
    id
    name
    displayName
    avatar
    service
    apiVersion
    isLocked
  }
`)

/**
 * `MentionCard` and `useChannelMap` still type their channel against the full
 * codegen `Channel`, so channels flowing through this list must satisfy both
 * them and the colocated fragment. The intersection collapses to the fragment
 * type once those consumers migrate (CORE-6384).
 */
export type MentionsListChannel = FragmentOf<typeof MentionsList_Channel> &
  React.ComponentProps<typeof MentionCard>['channel']

type MentionListItemProps = {
  mention: MentionData
  replies: MentionData[]
  channel: React.ComponentProps<typeof MentionCard>['channel'] | undefined
  isLastItem: boolean
  lastElementRef: ((node: HTMLElement | null) => void) | undefined
  onManualExpansion: (mentionId: string | null) => void
  onExpandNextMention: (fromMentionId?: string, onlyRepliable?: boolean) => void
  onExpandPreviousMention: () => void
  onLocalResolutionChange: (mentionId: string, isResolved: boolean) => void
  isNew: boolean
}

/**
 * Wraps a `MentionCard` in its context provider, skipping any mention whose
 * channel isn't in the channel map. Subscribes per-card to the coordination
 * store so a navigation change re-renders only the affected cards.
 */
const MentionListItem = ({
  mention,
  replies,
  channel,
  isLastItem,
  lastElementRef,
  onManualExpansion,
  onExpandNextMention,
  onExpandPreviousMention,
  onLocalResolutionChange,
  isNew,
}: MentionListItemProps): JSX.Element | null => {
  const expanded = useEngagementCoordinationStore(selectIsExpanded(mention.id))
  const isLocallyResolved = useEngagementCoordinationStore(
    selectIsLocallyResolved(mention.id),
  )

  // Skip rendering if channel not found (shouldn't happen in normal operation)
  if (!channel) {
    return null
  }

  return (
    <MentionContextProvider
      key={mention.id}
      mention={mention}
      replies={replies}
    >
      <MentionCard
        ref={isLastItem ? lastElementRef : undefined}
        channel={channel}
        expanded={expanded}
        onManualExpansion={onManualExpansion}
        onExpandNextMention={onExpandNextMention}
        onExpandPreviousMention={onExpandPreviousMention}
        onLocalResolutionChange={onLocalResolutionChange}
        isLocallyResolved={isLocallyResolved}
        isNew={isNew}
      />
    </MentionContextProvider>
  )
}

type MentionsListProps = {
  channels: MentionsListChannel[]
}

const FlexMotion = m.create(Flex)

export const MentionsList = React.memo(function MentionsList(
  props: MentionsListProps,
): JSX.Element {
  const {
    config: { hasSupportedMentionsChannel },
    loading: configLoading,
    error: configError,
  } = useCommunityConfig()
  const { channelsNeedingRefresh, allChannelsNeedRefresh } =
    useMentionsNeedsRefresh(props.channels)

  // The mentions tab shows on every channel, so the inbox has to say when none
  // of the channels in scope can produce mentions — otherwise it reads as an
  // inbox Buffer is filling. Checked before the feed mounts so no inbox query
  // fires for channels the backend would return nothing for.
  if (configLoading) {
    return <MentionsListSkeleton />
  }

  // Support reads as absent whenever the configuration is missing, so an
  // unreachable config would otherwise tell the user their channel can't do
  // mentions — a claim about their setup we haven't established.
  if (configError) {
    return <MentionsError errorMessage={configError} />
  }

  if (!hasSupportedMentionsChannel(props.channels)) {
    return <MentionsNotSupportedEmptyState />
  }

  // All-blocked means the whole inbox is dead until a reconnect — checked
  // before the feed mounts so no inbox query fires for channels the backend
  // collects nothing for. A partially blocked scope keeps the feed: the
  // healthy channels' mentions still flow. The views hide the header filters
  // off the same derivation.
  if (allChannelsNeedRefresh) {
    return <MentionsNeedsRefreshEmptyState channels={channelsNeedingRefresh} />
  }

  return (
    <EngagementCoordinationProvider>
      <MentionsListContent {...props} />
    </EngagementCoordinationProvider>
  )
})

function MentionsListContent({ channels }: MentionsListProps): JSX.Element {
  const {
    filters: { status, sortOption, publishedAt },
    utils: { clearFilters, hasActiveFilters },
  } = useFilters()

  const filters = React.useMemo<BaseCommunityFilters>(
    () => ({ status, sortOption, publishedAt }),
    [status, sortOption, publishedAt],
  )

  const { query, mentions } = useMentionsData({ channels, filters })
  const channelById = useChannelMap(channels)
  const noMediaChannels = useNoMediaChannels(channels, 'mention')

  // Replied mentions surface their replies below the main mention. We fetch
  // them for every replied card in one batched request (rather than per card)
  // and hand each card its own slice.
  const repliedMentionIds = React.useMemo(
    () =>
      mentions.list
        .filter((mention) => mention.status === 'replied')
        .map((mention) => mention.id),
    [mentions.list],
  )
  const repliesByMentionId = useMentionRepliesBatch(repliedMentionIds)

  const coordination = useMentionListCoordination({
    mentions: mentions.list,
    channelById,
    filters,
    isLoading: query.loading,
  })

  // Resolving the last card swaps in the caught-up state above the list, which
  // is easy to miss from further down a long feed.
  const containerRef = useScrollToTop(coordination.allLocallyResolved)

  // New items land at the top on every sort except oldest-first, where they
  // land at the bottom; the boundary orients the divider and scroll to match.
  const direction = sortOption === 'oldest' ? 'bottom' : 'top'
  const mentionIds = React.useMemo(
    () => mentions.list.map((mention) => mention.id),
    [mentions.list],
  )
  const { newlyArrivedIds, dividerIndex } = useNewEngagementBoundary({
    kind: 'mention',
    itemIds: mentionIds,
    direction,
    containerRef,
    resetKey: `${channels.map((channel) => channel.id).join(',')}|${status}|${sortOption}|${publishedAt}`,
  })

  const {
    EmptyState,
    loading: emptyStateLoading,
    showEmptyStateWithMentions,
  } = useMentionsEmptyState({
    channels,
    filters,
    hasActiveFilters,
    onClearFilters: clearFilters,
    hasMentionsInList: mentions.hasMentions,
    allMentionsLocallyResolved: coordination.allLocallyResolved,
  })

  const { loading, error, fetchingMore, lastElementRef } = query

  if (loading && !mentions.hasMentions) {
    return <MentionsListSkeleton />
  }

  if (error) {
    return <MentionsError errorMessage={error.message} />
  }

  if (!mentions.hasMentions) {
    // Hold the skeleton until the empty state is decided — the wrong one
    // flashing tells the user something untrue about their channel.
    return emptyStateLoading ? <MentionsListSkeleton /> : EmptyState
  }

  return (
    <FlexMotion ref={containerRef} direction="column" gap="md" fullWidth>
      {/* The hint leads the feed wherever it shows, so the caught-up state
          slotting in above the list must not push it down. */}
      <NoMediaHint noMediaChannels={noMediaChannels} engagementType="mention" />
      <AnimatePresence>
        {showEmptyStateWithMentions && EmptyState}
      </AnimatePresence>
      <m.ol
        aria-busy={fetchingMore}
        aria-label="Mentions list"
        className={styles.feed}
        tabIndex={0}
      >
        {mentions.list.map((mention, index) => (
          <React.Fragment key={mention.id}>
            {index === dividerIndex && (
              <NewItemsDivider
                label="New mentions"
                arrowDown={direction === 'bottom'}
              />
            )}
            <MentionListItem
              mention={mention}
              replies={repliesByMentionId.get(mention.id) ?? []}
              channel={channelById.get(mention.channelId)}
              isLastItem={index === mentions.list.length - 1}
              lastElementRef={lastElementRef}
              onManualExpansion={coordination.expand}
              onExpandNextMention={coordination.expandNext}
              onExpandPreviousMention={coordination.expandPrevious}
              onLocalResolutionChange={coordination.setResolution}
              isNew={newlyArrivedIds.has(mention.id)}
            />
          </React.Fragment>
        ))}
        {fetchingMore && (
          <li className={styles.loading}>
            <SimpleSpinner size="small" />
          </li>
        )}
      </m.ol>
    </FlexMotion>
  )
}

export const MentionsListSkeleton = (): JSX.Element => (
  <Flex direction="column" gap="md" fullWidth>
    {/* Keep the label a sibling of the <ol>; a list should only contain <li>. */}
    <VisuallyHidden>Loading mentions</VisuallyHidden>
    <ol aria-busy="true" aria-label="Mentions list" className={styles.feed}>
      {range(20).map((index) => (
        <MentionCardSkeleton key={index} />
      ))}
    </ol>
  </Flex>
)
